import React, { useState, useEffect, useMemo } from 'react';
import {
  getToolPartNumbers, getToolAssetTags, getToolMedia,
  getToolLifecycleProfile, getToolLifecycleAssessments, getToolDisposalWorkflow,
} from '../../api/assetRegistryApi';
import { comparePartNumbers, compareAssetTags } from '../../shared/utils/reconSort';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ── Helpers ───────────────────────────────────────────────────────────── */

function fmtDate(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString('en-US'); } catch { return v; }
}
function fmtDatetime(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }); } catch { return v; }
}
function fmtBool(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return null;
}

const CONDITION_LABELS = {
  1: '1 — As good as new',
  2: '2 — Good condition',
  3: '3 — Fair condition',
  4: '4 — Poor condition',
};

function statusBadgeClass(status) {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'ar-badge-success';
  if (s === 'LOW MVC') return 'ar-badge-lowmvc';
  if (['OBSOLETE', 'DISCON', 'SCRAPPED'].includes(s)) return 'ar-badge-danger';
  return 'ar-badge-neutral';
}

function dispBadgeClass(cat) {
  const s = (cat || '').toUpperCase();
  if (s === 'URGENT') return 'ar-badge-danger';
  if (s === 'NEAR_TERM') return 'ar-badge-warning';
  if (s === 'MONITORING') return 'ar-badge-success';
  return 'ar-badge-neutral';
}

/* ── Panel order persistence ───────────────────────────────────────────── */
const DEFAULT_ORDER = [
  'tool_id_panel', 'location', 'inventory_capture', 'status_flags',
  'part_numbers', 'asset_tags', 'photos', 'physical', 'supplier_feedback',
  'lifecycle', 'assessment_history', 'disposal_workflow', 'notes',
];

function getPanelOrderKey(email) {
  return `tmg_asset_detail_panel_order_${email || 'default'}`;
}
function loadPanelOrder(email) {
  try {
    const raw = localStorage.getItem(getPanelOrderKey(email));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && DEFAULT_ORDER.every(k => parsed.includes(k))) return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_ORDER;
}
function savePanelOrder(email, order) {
  try { localStorage.setItem(getPanelOrderKey(email), JSON.stringify(order)); } catch { /* ignore */ }
}

/* ── SortableSection ───────────────────────────────────────────────────── */

function SortableSection({ id, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className={`ar-panel-section${isDragging ? ' ar-panel-dragging' : ''}`}>
      <div className="ar-panel-header">
        <span className="ar-panel-drag" {...attributes} {...listeners}>&#10495;</span>
        <button className="ar-panel-toggle" onClick={() => setOpen(!open)} type="button" style={{ display: 'contents' }}>
          <span className="ar-panel-title">{title}</span>
        </button>
        <span className="ar-panel-header-right">
          <button className="ar-panel-chevron-btn" onClick={() => setOpen(!open)} type="button">
            <span className={`ar-panel-chevron ${open ? 'open' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
        </span>
      </div>
      {open && <div className="ar-panel-body">{children}</div>}
    </div>
  );
}

/* ── Field display ─────────────────────────────────────────────────────── */

function Field({ label, value }) {
  const display = (value === null || value === undefined || value === '')
    ? '\u2014'
    : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
  const isEmpty = display === '\u2014';
  return (
    <div className="ar-field">
      <span className="ar-field-label">{label}</span>
      <span className={isEmpty ? 'ar-field-empty' : 'ar-field-value'}>{display}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */

export default function AssetToolDetail({ tool, callerEmail, onClose }) {
  const [partNumbers, setPartNumbers] = useState([]);
  const [pnLoading, setPnLoading] = useState(false);
  const [assetTags, setAssetTags] = useState([]);
  const [atLoading, setAtLoading] = useState(false);
  const [media, setMedia] = useState([]);
  const [lifecycleProfile, setLifecycleProfile] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [disposalWorkflow, setDisposalWorkflow] = useState(null);
  const [extLoading, setExtLoading] = useState(false);
  const [panelOrder, setPanelOrder] = useState(() => loadPanelOrder(callerEmail));

  const toolId = tool?.id || tool?.tool_id;

  // Display-only sort: sorted COPIES for render (this screen is read-only).
  const sortedPartNumbers = useMemo(
    () => [...partNumbers].sort(comparePartNumbers), [partNumbers]);
  const sortedAssetTags = useMemo(
    () => [...assetTags].sort(compareAssetTags), [assetTags]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPanelOrder(prev => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        const next = arrayMove(prev, oldIdx, newIdx);
        savePanelOrder(callerEmail, next);
        return next;
      });
    }
  }

  /* Fetch part numbers + asset tags */
  useEffect(() => {
    if (!toolId || !callerEmail) return;
    setPnLoading(true);
    getToolPartNumbers(toolId, callerEmail)
      .then(data => setPartNumbers(Array.isArray(data) ? data : []))
      .catch(() => setPartNumbers([]))
      .finally(() => setPnLoading(false));
    setAtLoading(true);
    getToolAssetTags(toolId, callerEmail)
      .then(data => setAssetTags(Array.isArray(data) ? data : []))
      .catch(() => setAssetTags([]))
      .finally(() => setAtLoading(false));
  }, [toolId, callerEmail]);

  /* Fetch lifecycle, assessments, disposal in parallel */
  useEffect(() => {
    if (!toolId || !callerEmail) return;
    setExtLoading(true);
    Promise.all([
      getToolLifecycleProfile(toolId, callerEmail),
      getToolLifecycleAssessments(toolId, callerEmail),
      getToolDisposalWorkflow(toolId, callerEmail),
      getToolMedia(toolId, callerEmail),
    ])
      .then(([lp, asmt, dw, m]) => {
        setLifecycleProfile(lp || null);
        setAssessments(Array.isArray(asmt) ? asmt : []);
        setDisposalWorkflow(dw || null);
        setMedia(Array.isArray(m) ? m : []);
      })
      .catch(() => {})
      .finally(() => setExtLoading(false));
  }, [toolId, callerEmail]);

  if (!tool) return null;

  const prelimStatus = (tool.preliminary_tool_status || 'TBD').toUpperCase();
  const reconStatus = (tool.recon_status || 'OPEN').toUpperCase();
  const lp = lifecycleProfile;

  /* ── Location numbers ────────────────────────────────────────────────────
     The five *_nbr keys are the location vocabulary (field zone +
     FIELD_CAPTURE_BASE_ALLOWED). They render together when any one has a
     value and not at all when none does, so a tool with none shows only Site
     Location and Detail Location. Display only; no writer on this panel. */
  const NBR_FIELDS = ['row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr'];
  const hasValue = k => tool[k] !== null && tool[k] !== undefined && tool[k] !== '';
  const hasNbr = NBR_FIELDS.some(hasValue);

  /* ── Section map ─────────────────────────────────────────────────────── */
  const sectionMap = {
    tool_id_panel: (
      <SortableSection key="tool_id_panel" id="tool_id_panel" title="Tool Identification">
        <div className="ar-section-body">
          <Field label="Supplier Tool ID" value={tool.supplier_tool_id} />
          <Field label="Alt Tool ID" value={tool.alt_tool_id} />
          <Field label="Tool Type" value={tool.tool_type} />
          <Field label="Material" value={tool.material} />
          <Field label="OEM" value={tool.oem} />
          <Field label="Die Op" value={tool.die_op} />
          <Field label="Tool Code" value={tool.tool_code} />
          <Field label="CSPC" value={tool.cspc} />
          <Field label="Barcode" value={tool.barcode} />
          <Field label="QR Code" value={tool.qr_code} />
          <Field label="Platform" value={tool.platform} />
          <Field label="Quantity" value={tool.quantity} />
          <Field label="Cavities" value={tool.cavities} />
          <Field label="Part Description" value={tool.part_description} />
          <Field label="Tool Description" value={tool.tool_description} />
          <Field label="Tier Level" value={tool.tier_level} />
          <Field label="Tier DUNS" value={tool.tier_duns} />
          <Field label="List Number" value={tool.list_number} />
        </div>
      </SortableSection>
    ),

    location: (
      <SortableSection key="location" id="location" title="Location">
        <div className="ar-section-body">
          <Field label="Site Location" value={tool.site_location} />
          <Field label="Detail Location" value={tool.detail_location} />
          {hasNbr && (
            <>
              <Field label="Row Nbr" value={tool.row_nbr} />
              <Field label="Stack Nbr" value={tool.stack_nbr} />
              <Field label="Rack Nbr" value={tool.rack_nbr} />
              <Field label="Shelf Nbr" value={tool.shelf_nbr} />
              <Field label="Skid Nbr" value={tool.skid_nbr} />
            </>
          )}
          <Field label="Ship To Location" value={tool.ship_to_location} />
          <Field label="Last Ship Date" value={fmtDate(tool.last_ship_date)} />
        </div>
      </SortableSection>
    ),

    inventory_capture: (
      <SortableSection key="inventory_capture" id="inventory_capture" title="Inventory Capture">
        <div className="ar-section-body">
          <Field label="Found" value={fmtBool(tool.found)} />
          <Field label="Added By TMG" value={fmtBool(tool.added_by_tmg)} />
          <Field label="OEM Part Number Status" value={tool.inv_oem_pn_status} />
          <Field label="Asset Tag Status" value={tool.inv_asset_tag_status} />
          <Field label="Inventory Comments" value={tool.inventory_comments} />
        </div>
      </SortableSection>
    ),

    status_flags: (
      <SortableSection key="status_flags" id="status_flags" title="Status &amp; Flags">
        <div className="ar-section-body">
          <div className="ar-field">
            <span className="ar-field-label">Preliminary Status</span>
            <span className={`ar-badge ${statusBadgeClass(tool.preliminary_tool_status)}`}>{prelimStatus}</span>
          </div>
          <Field label="Workflow Status" value={tool.workflow_status} />
          <Field label="Disposal Recon Status" value={tool.recon_status} />
          <Field label="Approved Scrap" value={fmtBool(tool.approved_scrap)} />
          <Field label="Capacity Flag" value={fmtBool(tool.capacity_flag)} />
          <Field label="NPN Flag" value={fmtBool(tool.npn_flag)} />
          <Field label="Missing From Last Import" value={fmtBool(tool.missing_from_last_import)} />
        </div>
      </SortableSection>
    ),

    part_numbers: (
      <SortableSection key="part_numbers" id="part_numbers" title="Part Numbers">
        <div className="ar-list-section">
          {pnLoading ? (
            <div className="ar-list-loading">Loading part numbers…</div>
          ) : sortedPartNumbers.length === 0 ? (
            <div className="ar-list-empty">No part numbers on record</div>
          ) : (
            sortedPartNumbers.map((pn, i) => (
              <div key={pn.id || i} className="ar-list-item">
                {pn.part_number}
                {pn.part_number_type && <span className="ar-list-type">{pn.part_number_type}</span>}
              </div>
            ))
          )}
        </div>
      </SortableSection>
    ),

    asset_tags: (
      <SortableSection key="asset_tags" id="asset_tags" title="Asset Tags">
        <div className="ar-list-section">
          {atLoading ? (
            <div className="ar-list-loading">Loading asset tags…</div>
          ) : sortedAssetTags.length === 0 ? (
            <div className="ar-list-empty">No asset tags on record</div>
          ) : (
            sortedAssetTags.map((at, i) => (
              <div key={at.id || i} className="ar-list-item">
                {at.tag_value || at.asset_tag_value || '\u2014'}
              </div>
            ))
          )}
        </div>
      </SortableSection>
    ),

    photos: media.filter(m => m.blob_url).length > 0 ? (
      <SortableSection key="photos" id="photos" title="Photos" defaultOpen={false}>
        <div className="ar-photo-grid">
          {media.filter(m => m.blob_url).map((m, i) => (
            <div key={m.id || i} className="ar-photo-thumb-wrap">
              <button
                className="ar-photo-thumb"
                onClick={() => window.open(m.blob_url, '_blank')}
                title={m.file_name || 'View photo'}
              >
                <img src={m.blob_url} alt="" className="ar-photo-img" loading="lazy" />
              </button>
              {m.file_name && <span className="ar-photo-caption">{m.file_name}</span>}
            </div>
          ))}
        </div>
      </SortableSection>
    ) : null,

    physical: (
      <SortableSection key="physical" id="physical" title="Physical">
        <div className="ar-section-body">
          <Field label="Length" value={tool.length} />
          <Field label="Width" value={tool.width} />
          <Field label="Height" value={tool.height} />
          <Field label="Weight" value={
            tool.estimated_weight ? `${tool.estimated_weight}${tool.weight_unit ? ` ${tool.weight_unit}` : ''}` : null
          } />
        </div>
      </SortableSection>
    ),

    supplier_feedback: (
      <SortableSection key="supplier_feedback" id="supplier_feedback" title="Supplier Feedback">
        <div className="ar-section-body">
          <Field label="Supplier Status" value={tool.supplier_status} />
          <Field label="Supplier Comments" value={tool.supplier_comments} />
          <Field label="Supplier Confirmed Date" value={fmtDate(tool.supplier_confirmed_date)} />
          <Field label="Supplier Confirmed By" value={tool.supplier_confirmed_by} />
          <Field label="Retained Reason" value={tool.retained_reason} />
        </div>
      </SortableSection>
    ),

    lifecycle: lp ? (
      <SortableSection key="lifecycle" id="lifecycle" title="Lifecycle">
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <span className={`ar-badge ${lp.recon_status === 'CLOSED' ? 'ar-badge-neutral' : 'ar-badge-success'}`}>
            LIFECYCLE RECON: {(lp.recon_status || 'OPEN').toUpperCase()}
          </span>
        </div>

        {/* Pieces & Capacity */}
        <div className="ar-sub-heading">Pieces &amp; Capacity</div>
        <div className="ar-section-body">
          <Field label="Warranty Pieces" value={lp.warranty_shots} />
          <Field label="Warranty Source" value={lp.warranty_shots_source} />
          <Field label="Actual Pieces" value={lp.actual_shots_to_date} />
          <Field label="Pieces Remaining" value={lp.shots_remaining} />
          <Field label="Weekly Capacity" value={lp.weekly_capacity} />
          <Field label="Demand Weekly Average" value={lp.demand_weekly_average} />
          <Field label="Pieces Last Updated" value={fmtDatetime(lp.shots_last_updated_date)} />
          <Field label="Last Updated By" value={lp.shots_last_updated_by} />
        </div>

        {/* EOW */}
        <div className="ar-sub-heading">EOW</div>
        <div className="ar-section-body">
          <Field label="EOW Estimated Date" value={fmtDate(lp.eow_estimated_date)} />
          <Field label="EOW Decision Trigger Date" value={fmtDate(lp.eow_decision_trigger_date)} />
          <div className="ar-field">
            <span className="ar-field-label">Disposition Category</span>
            {lp.disposition_category ? (
              <span className={`ar-badge ${dispBadgeClass(lp.disposition_category)}`}>{lp.disposition_category}</span>
            ) : (
              <span className="ar-field-empty">{'\u2014'}</span>
            )}
          </div>
          <Field label="EOW Last Calculated" value={fmtDatetime(lp.eow_last_calculated_date)} />
          <Field label="EOW Skip Reason" value={lp.eow_skip_reason} />
          <Field label="SCR Lead Time Used" value={lp.eow_scr_lead_time_weeks_used} />
        </div>

        {/* Condition */}
        <div className="ar-sub-heading">Condition</div>
        <div className="ar-section-body">
          <Field label="Current Condition Score" value={CONDITION_LABELS[lp.current_condition_score] || lp.current_condition_score} />
          <Field label="Condition Last Assessed" value={fmtDatetime(lp.condition_last_assessed_date)} />
          <Field label="Condition Last Assessed By" value={lp.condition_last_assessed_by} />
        </div>

        {/* Asset Value */}
        <div className="ar-sub-heading">Asset Value</div>
        <div className="ar-section-body">
          <Field label="Original Asset Value" value={
            lp.original_asset_value ? `${lp.original_asset_value}${lp.original_asset_currency ? ` ${lp.original_asset_currency}` : ''}` : null
          } />
          <Field label="Cost Type" value={lp.cost_type} />
          <Field label="Ownership" value={lp.ownership} />
        </div>

        {/* Other */}
        <div className="ar-sub-heading">Other</div>
        <div className="ar-section-body">
          <Field label="Engagement Type" value={lp.engagement_type} />
          <Field label="Service Type" value={lp.service_type} />
          <Field label="Shift Pattern" value={lp.shift_pattern} />
          <Field label="Cavity Number Start" value={lp.cavity_number_start} />
          <Field label="Cavity Number End" value={lp.cavity_number_end} />
          <Field label="Flag Yellow" value={fmtBool(lp.flag_yellow)} />
          <Field label="Flag Orange" value={fmtBool(lp.flag_orange)} />
          <Field label="Agreed Solution" value={lp.agreed_solution} />
          <Field label="Finished Goods On Hand" value={lp.finished_goods_on_hand} />
          <Field label="Work In Process" value={lp.work_in_process} />
          <Field label="Raw Materials On Hand" value={lp.raw_materials_on_hand} />
          <Field label="Lifecycle Detail" value={lp.lifecycle_detail} />
          <Field label="Predecessor Tool ID" value={lp.predecessor_tool_id} />
        </div>

        {/* SCR */}
        <div className="ar-sub-heading">SCR</div>
        <div className="ar-section-body">
          <Field label="SCR Status" value={lp.scr_status} />
          <Field label="SCR Type" value={lp.scr_type} />
          <Field label="SCR Submitted Date" value={fmtDate(lp.scr_submitted_date)} />
          <Field label="SCR Identifier" value={lp.scr_identifier} />
          <Field label="SCR Notes" value={lp.scr_notes} />
        </div>
      </SortableSection>
    ) : null,

    assessment_history: assessments.length > 0 ? (
      <SortableSection key="assessment_history" id="assessment_history" title="Assessment History">
        <div className="ar-list-section">
          {assessments.slice(0, 10).map((a, i) => (
            <div key={a.id || i} className="ar-assessment-card">
              <div className="ar-assessment-header">
                <span className="ar-field-value">{fmtDatetime(a.assessment_date)}</span>
                <span className="ar-badge ar-badge-neutral">{a.assessment_type}</span>
              </div>
              <div className="ar-assessment-body">
                {a.assessed_by && <div className="ar-assessment-meta">By: {a.assessed_by}</div>}
                {a.assessment_type === 'CONDITION_REVIEW' && a.condition_score_previous !== a.condition_score_new && (
                  <div className="ar-assessment-change">Condition Score: {a.condition_score_previous} → {a.condition_score_new}</div>
                )}
                {a.assessment_type === 'SHOTS_UPDATE' && a.actual_shots_previous !== a.actual_shots_new && (
                  <div className="ar-assessment-change">Actual Pieces: {a.actual_shots_previous?.toLocaleString()} → {a.actual_shots_new?.toLocaleString()}</div>
                )}
                {a.warranty_shots_previous != null && a.warranty_shots_new != null && a.warranty_shots_previous !== a.warranty_shots_new && (
                  <div className="ar-assessment-change">Warranty Pieces: {a.warranty_shots_previous?.toLocaleString()} → {a.warranty_shots_new?.toLocaleString()}</div>
                )}
                {a.disposition_category_previous && a.disposition_category_new && a.disposition_category_previous !== a.disposition_category_new && (
                  <div className="ar-assessment-change">Disposition: {a.disposition_category_previous} → {a.disposition_category_new}</div>
                )}
                {a.assessment_notes && <div className="ar-assessment-notes">{a.assessment_notes}</div>}
                {a.scr_decision && <div className="ar-assessment-notes">SCR Decision: {a.scr_decision}{a.scr_rationale ? ` — ${a.scr_rationale}` : ''}</div>}
              </div>
            </div>
          ))}
          {assessments.length > 10 && (
            <div className="ar-list-empty">Showing 10 of {assessments.length}</div>
          )}
        </div>
      </SortableSection>
    ) : null,

    disposal_workflow: disposalWorkflow ? (
      <SortableSection key="disposal_workflow" id="disposal_workflow" title="Disposal Workflow">
        <div className="ar-section-body">
          <Field label="Marked Status" value={disposalWorkflow.marked} />
          <Field label="Staged Status" value={disposalWorkflow.staged} />
          <Field label="Scrap Status" value={disposalWorkflow.scrap_status} />
          <Field label="Scrap Project Number" value={disposalWorkflow.scrap_project_number} />
          <Field label="Scrap Project Comments" value={disposalWorkflow.scrap_project_comments} />
          <Field label="Ship Date" value={fmtDate(disposalWorkflow.ship_date)} />
          <Field label="Truck Number" value={disposalWorkflow.truck_nbr} />
          <Field label="Day Trip Number" value={disposalWorkflow.day_trip_nbr} />
          <Field label="BOL Number" value={disposalWorkflow.bol_nbr} />
          <Field label="Disposal Comments" value={disposalWorkflow.disposal_comments} />
        </div>
      </SortableSection>
    ) : null,

    notes: (
      <SortableSection key="notes" id="notes" title="Notes &amp; Comments">
        <div className="ar-section-body" style={{ gridTemplateColumns: '1fr' }}>
          <Field label="Inventory Comments" value={tool.inventory_comments} />
          <Field label="TMG Data Comments" value={tool.tmg_data_comments} />
          {lp && <Field label="Lifecycle Detail" value={lp.lifecycle_detail} />}
        </div>
      </SortableSection>
    ),
  };

  return (
    <div className="ar-detail">
      {/* Sticky header */}
      <div className="ar-detail-header-strip">
        <div className="ar-detail-header-top">
          <span className="ar-detail-toolid">{tool.supplier_tool_id || tool.tool_id}</span>
          {tool.alt_tool_id && <span className="ar-detail-altid">{tool.alt_tool_id}</span>}
          <button className="ar-detail-close" onClick={onClose} title="Close">×</button>
        </div>
        <div className="ar-detail-badges">
          {tool.oem && <span className="ar-badge ar-badge-neutral">OEM: {tool.oem}</span>}
          <span className={`ar-badge ${statusBadgeClass(tool.preliminary_tool_status)}`}>STATUS: {prelimStatus}</span>
          <span className="ar-badge ar-badge-neutral">RECON: {reconStatus}</span>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="ar-detail-body">
        {extLoading && (
          <div style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
            Loading additional data…
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={panelOrder} strategy={verticalListSortingStrategy}>
            {panelOrder.map(id => sectionMap[id]).filter(Boolean)}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
